import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';

interface ParBalanceRow {
  id: string;
  camperName: string;
  email: string;
  parAvailable: number;
  parSpent: number;
}

const getTotalParLeft = (parAvailable: number, parSpent: number): number => {
  const difference = parAvailable - parSpent;
  return difference > 0 ? difference : 0;
};

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

const getCamperNameFromPrimaryCamper = (data: any): string => {
  const firstName = String(data?.FirstName || data?.firstName || '').trim();
  const lastName = String(data?.LastName || data?.lastName || '').trim();
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) return fullName;
  return String(data?.primaryCamperName || '').trim() || 'Unknown Camper';
};

const getCamperNameFromRegistration = (data: any): string => {
  const registrationName = String(data?.registrationInfo?.primaryCamperName || '').trim();
  if (registrationName) return registrationName;

  const firstName = String(data?.firstName || '').trim();
  const lastName = String(data?.lastName || '').trim();
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) return fullName;

  return 'Unknown Camper';
};

const isParPayment = (method: string): boolean => String(method || '').trim().toLowerCase() === 'par';

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD'
  }).format(amount);

const ParBalancesReport: React.FC = () => {
  const [rows, setRows] = useState<ParBalanceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchParBalances();
  }, []);

  const fetchParBalances = async () => {
    try {
      setLoading(true);
      setError('');

      const [primaryCampersSnapshot, registrationsSnapshot] = await Promise.all([
        getDocs(collection(db, 'primaryCampers')),
        getDocs(collection(db, 'Registrations'))
      ]);

      const byKey = new Map<string, ParBalanceRow>();

      primaryCampersSnapshot.forEach((docSnap) => {
        const data = docSnap.data() as any;
        const email = String(data?.email || data?.Email || '').trim();
        const key = email ? normalizeEmail(email) : `primary:${docSnap.id}`;
        const parAvailable = Number(data?.par) || 0;
        const camperName = getCamperNameFromPrimaryCamper(data);

        if (!byKey.has(key)) {
          byKey.set(key, {
            id: key,
            camperName,
            email,
            parAvailable,
            parSpent: 0
          });
          return;
        }

        const existing = byKey.get(key)!;
        existing.parAvailable = Math.max(existing.parAvailable, parAvailable);
        if (!existing.camperName || existing.camperName === 'Unknown Camper') {
          existing.camperName = camperName;
        }
        if (!existing.email && email) {
          existing.email = email;
        }
      });

      registrationsSnapshot.forEach((docSnap) => {
        const data = docSnap.data() as any;
        const payments = Array.isArray(data?.paymentHistory) ? data.paymentHistory : [];
        if (payments.length === 0) return;

        const parSpent = payments.reduce((sum: number, payment: any) => {
          if (!isParPayment(payment?.method)) return sum;
          return sum + (Number(payment?.amount) || 0);
        }, 0);

        if (parSpent <= 0) return;

        const email = String(data?.registrationInfo?.email || data?.email || '').trim();
        const key = email ? normalizeEmail(email) : `registration:${docSnap.id}`;
        const camperName = getCamperNameFromRegistration(data);

        if (!byKey.has(key)) {
          byKey.set(key, {
            id: key,
            camperName,
            email,
            parAvailable: 0,
            parSpent
          });
          return;
        }

        const existing = byKey.get(key)!;
        existing.parSpent += parSpent;
        if (!existing.camperName || existing.camperName === 'Unknown Camper') {
          existing.camperName = camperName;
        }
        if (!existing.email && email) {
          existing.email = email;
        }
      });

      const filteredRows = Array.from(byKey.values())
        .filter((row) => row.parAvailable > 0 || row.parSpent > 0)
        .sort((a, b) => a.camperName.localeCompare(b.camperName));

      setRows(filteredRows);
    } catch (err) {
      console.error('Error fetching PAR balances report:', err);
      setError('Failed to load PAR balances report');
    } finally {
      setLoading(false);
    }
  };

  const exportToCsv = () => {
    const headers = ['Camper Name', 'Email', 'Par Available', 'Par Spent', 'Total Par Left'];
    const csvRows = [
      headers.join(','),
      ...rows.map((row) =>
        [
          `"${row.camperName}"`,
          `"${row.email}"`,
          row.parAvailable.toFixed(2),
          row.parSpent.toFixed(2),
          getTotalParLeft(row.parAvailable, row.parSpent).toFixed(2)
        ].join(',')
      )
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `par-balances-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <div>Loading PAR balances...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="accounts-receivable-report">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}
      >
        <div>
          <h2>PAR Balances</h2>
          <p>Campers with PAR available and/or PAR spent this camp period.</p>
        </div>
        {rows.length > 0 && (
          <button
            style={{
              padding: '8px 16px',
              background: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500',
              fontSize: '0.9rem',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#218838')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#28a745')}
            onClick={exportToCsv}
          >
            Export to CSV
          </button>
        )}
      </div>

      <table className="receivable-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Camper Name</th>
            <th style={{ textAlign: 'left' }}>Email</th>
            <th style={{ textAlign: 'right' }}>Par Available</th>
            <th style={{ textAlign: 'right' }}>Par Spent</th>
            <th style={{ textAlign: 'right' }}>Total Par Left</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} style={{ borderBottom: '1px solid #ddd' }}>
              <td style={{ padding: '8px' }}>{row.camperName}</td>
              <td style={{ padding: '8px' }}>{row.email || '-'}</td>
              <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(row.parAvailable)}</td>
              <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(row.parSpent)}</td>
              <td style={{ padding: '8px', textAlign: 'right' }}>
                {formatCurrency(getTotalParLeft(row.parAvailable, row.parSpent))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {rows.length === 0 && <div>No PAR balances found for this camp period.</div>}
    </div>
  );
};

export default ParBalancesReport;
